import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Mail, 
  Settings, 
  LogOut,
  FileText,
  Package,
  BarChart3,
  ChevronRight,
  Eye,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ClassTypesManager, ClassScheduler } from '../components/ClassManagement';
import FAQsTab from '../components/admin/FAQsTab';
import ChatTab from '../components/admin/ChatTab';
import SpamTab from '../components/admin/SpamTab';
import AccessTab from '../components/admin/AccessTab';
import Buyers25Tab from '../components/admin/Buyers25Tab';
import InterestTab from '../components/admin/InterestTab';
import SiteFilesTab from '../components/admin/SiteFilesTab';
import axios from 'axios';
import DOMPurify from 'dompurify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    signedWaivers: 0,
    emailSubscribers: 0,
    upcomingClasses: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check authentication (cookie-based; local flags for UI gating)
    const userData = localStorage.getItem('user');
    const isAuthed = localStorage.getItem('isAuthenticated') === 'true';
    
    if (!isAuthed || !userData) {
      navigate('/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Fetch stats (server enforces admin role)
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (err) {
      console.log('Stats fetch error:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
        return;
      }
      // Use mock data if endpoint not ready
      setStats({
        totalUsers: 12,
        signedWaivers: 8,
        emailSubscribers: 25,
        upcomingClasses: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('Logout API call failed:', err);
    }
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    navigate('/login');
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'classes', label: 'Classes', icon: Calendar },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'waivers', label: 'Waivers', icon: FileText },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle },
    { id: 'chat', label: 'Ask Cal Chat', icon: Mail },
    { id: 'spam', label: 'Spam Shield', icon: Settings },
    { id: 'access25', label: '25 for 25', icon: BarChart3 },
    { id: 'buyers25', label: '25 Buyers', icon: Users },
    { id: 'interest', label: 'Interest Lists', icon: FileText },
    { id: 'sitefiles', label: 'Site Files', icon: FileText },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'emails', label: 'Email Preview', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>CAL FIT</h2>
          <span>Admin Dashboard</span>
        </div>
        
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => setActiveTab(item.id)}
                data-testid={`nav-${item.id}`}
              >
                <IconComponent size={20} />
                <span>{item.label}</span>
                <ChevronRight size={16} className="chevron" />
              </button>
            );
          })}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <span className="user-email">{user?.email}</span>
            <span className="user-role">Administrator</span>
          </div>
          <Button 
            variant="ghost" 
            className="logout-btn"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {activeTab === 'overview' && (
          <OverviewTab stats={stats} setActiveTab={setActiveTab} />
        )}
        {activeTab === 'classes' && <ClassesTab />}
        {activeTab === 'clients' && <ClientsTab />}
        {activeTab === 'waivers' && <WaiversTab />}
        {activeTab === 'faqs' && <FAQsTab />}
        {activeTab === 'chat' && <ChatTab />}
        {activeTab === 'spam' && <SpamTab />}
        {activeTab === 'access25' && <AccessTab />}
        {activeTab === 'buyers25' && <Buyers25Tab />}
        {activeTab === 'interest' && <InterestTab />}
        {activeTab === 'sitefiles' && <SiteFilesTab />}
        {activeTab === 'packages' && <PackagesTab />}
        {activeTab === 'emails' && <EmailPreviewTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </main>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ stats, setActiveTab }) => {
  const statCards = [
    { label: 'Total Users', value: stats.totalUsers, icon: Users, color: '#FF6B35' },
    { label: 'Signed Waivers', value: stats.signedWaivers, icon: FileText, color: '#22C55E' },
    { label: 'Email Subscribers', value: stats.emailSubscribers, icon: Mail, color: '#3B82F6' },
    { label: 'Upcoming Classes', value: stats.upcomingClasses, icon: Calendar, color: '#FFC914' },
  ];

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h1>Dashboard Overview</h1>
        <p>Welcome back! Here's what's happening with GoCalFit.</p>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Card key={index} className="stat-card">
              <CardContent className="stat-content">
                <div className="stat-icon" style={{ backgroundColor: `${stat.color}20` }}>
                  <IconComponent size={24} style={{ color: stat.color }} />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Button className="action-btn" onClick={() => setActiveTab('classes')}>
            <Calendar size={20} />
            Add New Class
          </Button>
          <Button className="action-btn" onClick={() => setActiveTab('emails')}>
            <Eye size={20} />
            Preview Emails
          </Button>
          <Button className="action-btn" onClick={() => setActiveTab('clients')}>
            <Users size={20} />
            View Clients
          </Button>
        </div>
      </div>
    </div>
  );
};

// Classes Tab - Full Implementation
const ClassesTab = () => {
  const [subTab, setSubTab] = useState('types');
  
  return (
    <div className="tab-content">
      <div className="tab-header">
        <h1>Class Management</h1>
        <p>Create class types and schedule classes.</p>
      </div>
      
      <div className="sub-tabs">
        <button 
          className={`sub-tab ${subTab === 'types' ? 'active' : ''}`}
          onClick={() => setSubTab('types')}
        >
          Class Types
        </button>
        <button 
          className={`sub-tab ${subTab === 'schedule' ? 'active' : ''}`}
          onClick={() => setSubTab('schedule')}
        >
          Schedule
        </button>
      </div>
      
      {subTab === 'types' ? <ClassTypesManager /> : <ClassScheduler />}
    </div>
  );
};

// Clients Tab (Placeholder)
const ClientsTab = () => (
  <div className="tab-content">
    <div className="tab-header">
      <h1>Client Management</h1>
      <p>View and manage your clients.</p>
    </div>
    <Card className="placeholder-card">
      <CardContent className="placeholder-content">
        <Users size={48} />
        <h3>Coming Soon</h3>
        <p>View client profiles, bookings, packages, and waiver status.</p>
      </CardContent>
    </Card>
  </div>
);

// Waivers Tab (Placeholder)
const WaiversTab = () => (
  <div className="tab-content">
    <div className="tab-header">
      <h1>Signed Waivers</h1>
      <p>View all signed waivers.</p>
    </div>
    <Card className="placeholder-card">
      <CardContent className="placeholder-content">
        <FileText size={48} />
        <h3>Coming Soon</h3>
        <p>View and download signed waivers, search by client name or date.</p>
      </CardContent>
    </Card>
  </div>
);

// Packages Tab (Placeholder)
const PackagesTab = () => (
  <div className="tab-content">
    <div className="tab-header">
      <h1>Class Packages</h1>
      <p>Create and manage class packages.</p>
    </div>
    <Card className="placeholder-card">
      <CardContent className="placeholder-content">
        <Package size={48} />
        <h3>Coming Soon</h3>
        <p>Create multi-class packs, subscriptions, and gift certificates.</p>
      </CardContent>
    </Card>
  </div>
);

// Email Preview Tab
const EmailPreviewTab = () => {
  const [selectedEmail, setSelectedEmail] = useState('welcome');
  
  const emailTypes = [
    { id: 'welcome', name: 'Welcome Email', description: 'Sent after new user signup' },
    { id: 'waiver', name: 'Waiver Confirmation', description: 'Sent after waiver is signed' },
    { id: 'booking', name: 'Booking Confirmation', description: 'Sent after class booking' },
    { id: 'subscriber', name: 'Subscriber Welcome', description: 'Sent from landing page popup' },
  ];

  const emailPreviews = {
    welcome: `
      <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #FF6B35; font-size: 28px;">Welcome to GoCalFit, [First Name]! 💫</h1>
        <p>We're thrilled to have you join our community!</p>
        <p><strong>Movement that lifts you. Play that grounds you.</strong></p>
        <p>You're one step closer to reclaiming your movement and remembering who you are. Here's what to do next:</p>
        <ol>
          <li>Complete your waiver (one-time, stored forever)</li>
          <li>Browse our class schedule</li>
          <li>Book your first flight!</li>
        </ol>
        <div style="text-align: center; margin: 30px 0;">
          <a href="#" style="background: linear-gradient(135deg, #FF6B35, #FFC914); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold;">Explore Classes</a>
        </div>
        <p style="color: #6B6B6B;">Questions? Reply to this email or visit us at:<br>The Studio inside Anytime Fitness<br>3560 New Centre Dr, Ste 70<br>Wilmington, NC 28403</p>
        <p>Can't wait to see you inside!<br><span style="color: #FF6B35;">Love, Cal 💫</span></p>
        <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
        <p style="font-size: 13px; color: #999; text-align: center;"><strong>Don't see this email in your inbox?</strong><br>Check your spam/junk folder and move this email to your inbox.<br>Add <strong>hello@gocalfit.com</strong> to your contacts!</p>
      </div>
    `,
    waiver: `
      <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #FF6B35; font-size: 24px;">Waiver Signed Successfully</h1>
        <p>This confirms that the CAL FIT Release and Waiver of Liability Agreement has been signed.</p>
        <div style="background: #FDFBF7; border-left: 4px solid #FF6B35; padding: 20px; margin: 20px 0; border-radius: 0 8px 8px 0;">
          <h2 style="margin-top: 0; font-size: 18px; color: #2A2A2A;">Waiver Details</h2>
          <p><strong>Participant:</strong> [Participant Name]</p>
          <p><strong>Signed By:</strong> [Signer Name]</p>
          <p><strong>Date Signed:</strong> [Date]</p>
          <p><strong>Confirmation ID:</strong> [WAIVER-ID]</p>
        </div>
        <p style="color: #6B6B6B;">This waiver remains in effect for all future visits and activities at CAL FIT.</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="#" style="background: linear-gradient(135deg, #FF6B35, #FFC914); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold;">Book Your First Class</a>
        </div>
        <p>See you soon!<br><span style="color: #FF6B35;">Cal & Team</span></p>
      </div>
    `,
    booking: `
      <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #FF6B35; font-size: 28px;">You're Booked! 🎉</h1>
        <p>Hey [First Name],</p>
        <p>Your class is confirmed! Get ready to fly.</p>
        <div style="background: #FDFBF7; border-left: 4px solid #FF6B35; padding: 20px; margin: 20px 0;">
          <h2 style="margin-top: 0; color: #2A2A2A;">Class Details</h2>
          <p><strong>Class:</strong> [Class Name]</p>
          <p><strong>Date:</strong> [Date]</p>
          <p><strong>Time:</strong> [Time]</p>
          <p><strong>Location:</strong> The Studio inside Anytime Fitness</p>
        </div>
        <h3>What to Bring:</h3>
        <ul>
          <li>Water bottle</li>
          <li>Comfortable workout clothes</li>
          <li>Positive energy!</li>
        </ul>
        <p style="color: #6B6B6B;"><strong>Need to cancel?</strong> Please give us at least 12 hours notice to avoid charges.</p>
        <p>See you soon!<br><span style="color: #FF6B35;">Cal & Team 💪</span></p>
      </div>
    `,
    subscriber: `
      <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #FF6B35; font-size: 28px;">Hey [First Name]! 💫</h1>
        <p>Thanks for joining the GoCalFit community!</p>
        <p><strong>Movement that lifts you. Play that grounds you.</strong></p>
        <p>You'll be the first to know about:</p>
        <ul>
          <li>New class schedules & openings</li>
          <li>Special events & workshops</li>
          <li>Exclusive offers for our community</li>
          <li>Tips for movement & wellness</li>
        </ul>
        <div style="text-align: center; margin: 30px 0;">
          <a href="#" style="background: linear-gradient(135deg, #FF6B35, #FFC914); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold;">Explore Our Classes</a>
        </div>
        <p>Can't wait to see you!<br><span style="color: #FF6B35;">Love, Cal 💫</span></p>
        <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
        <p style="font-size: 13px; color: #999; text-align: center;"><strong>Don't see this email in your inbox?</strong><br>Check your spam/junk folder and add <strong>hello@gocalfit.com</strong> to your contacts!</p>
      </div>
    `
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h1>Email Preview</h1>
        <p>Preview how your automated emails will look to recipients.</p>
      </div>

      <div className="email-preview-container">
        <div className="email-selector">
          <h3>Email Types</h3>
          {emailTypes.map((type) => (
            <button
              key={type.id}
              className={`email-type-btn ${selectedEmail === type.id ? 'active' : ''}`}
              onClick={() => setSelectedEmail(type.id)}
              data-testid={`email-type-${type.id}`}
            >
              <Mail size={18} />
              <div className="email-type-info">
                <span className="email-type-name">{type.name}</span>
                <span className="email-type-desc">{type.description}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="email-preview-panel">
          <div className="preview-header">
            <span className="preview-label">Preview</span>
            <span className="preview-from">From: Cal | GoCalFit &lt;hello@gocalfit.com&gt;</span>
          </div>
          <div 
            className="email-preview-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailPreviews[selectedEmail]) }}
          />
        </div>
      </div>
    </div>
  );
};

// Settings Tab with Categories Manager
const SettingsTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', color: '#6B7280' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchCategories = async () => {
    try {
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/categories`, {
        withCredentials: true
      });
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newCategory.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      
      await axios.post(
        `${BACKEND_URL}/api/admin/categories`,
        null,
        {
          withCredentials: true,
          params: { name: newCategory.name, color: newCategory.color }
        }
      );
      setSuccess('Category created successfully!');
      setNewCategory({ name: '', color: '#6B7280' });
      setShowAddForm(false);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      
      await axios.delete(`${BACKEND_URL}/api/admin/categories/${categoryId}`, {
        withCredentials: true
      });
      setSuccess('Category deleted successfully!');
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete category');
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h1>Settings</h1>
        <p>Configure your studio settings and categories.</p>
      </div>

      {success && <div className="success-message">{success}</div>}
      {error && <div className="error-message">{error}</div>}

      <Card className="settings-card">
        <CardHeader>
          <CardTitle className="settings-title">
            <span>Class Categories</span>
            <Button 
              size="sm" 
              onClick={() => setShowAddForm(!showAddForm)}
              className="add-category-btn"
            >
              {showAddForm ? 'Cancel' : '+ Add Category'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {showAddForm && (
            <form onSubmit={handleAddCategory} className="add-category-form">
              <div className="form-row">
                <div className="form-group">
                  <Label>Category Name</Label>
                  <Input
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Summer Camp"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <Label>Color</Label>
                  <div className="color-input-row">
                    <input
                      type="color"
                      value={newCategory.color}
                      onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                      className="color-picker"
                    />
                    <Input
                      value={newCategory.color}
                      onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                      className="color-text"
                    />
                  </div>
                </div>
                <Button type="submit" className="save-category-btn">
                  Save
                </Button>
              </div>
            </form>
          )}

          {loading ? (
            <p>Loading categories...</p>
          ) : (
            <div className="categories-list">
              {categories.map((cat) => (
                <div key={cat.id} className="category-item">
                  <div 
                    className="category-color-dot" 
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className="category-name">{cat.name}</span>
                  <span className="category-id">({cat.id})</span>
                  {cat.is_default ? (
                    <span className="default-badge">Default</span>
                  ) : (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="delete-category-btn"
                      onClick={() => handleDeleteCategory(cat.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="settings-card">
        <CardHeader>
          <CardTitle>Studio Information</CardTitle>
        </CardHeader>
        <CardContent className="placeholder-content">
          <p>Studio hours, location, and contact info settings coming soon.</p>
        </CardContent>
      </Card>

      <Card className="settings-card">
        <CardHeader>
          <CardTitle>Booking Policies</CardTitle>
        </CardHeader>
        <CardContent className="placeholder-content">
          <p>Cancellation policies, booking windows, and waitlist settings coming soon.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
